import { gsap } from "gsap/all";
import { ScrollTrigger, SplitText, ScrollSmoother } from "gsap/all";
import Swiper from "swiper";
import { Pagination } from "swiper/modules";

gsap.registerPlugin(ScrollTrigger, SplitText, ScrollSmoother);

const setupPageLoader = () => {
	const root = document.documentElement;
	const loaderEl = document.getElementById("page-loader");
	const barEl = document.getElementById("page-loader-bar");
	const percentEl = document.getElementById("page-loader-percent");
	if (!loaderEl || !barEl || !percentEl) {
		root.classList.remove("is-loading");
		return;
	}

	const clamp01 = (n) => Math.min(1, Math.max(0, n));
	const setProgress = (value01) => {
		const v = clamp01(value01);
		const pct = Math.round(v * 100);
		loaderEl.style.setProperty("--loader-progress", `${pct}%`);
		percentEl.textContent = `${pct}%`;
	};

	const MIN_VISIBLE_MS = 650;
	const startTime = performance.now();

	const images = Array.from(document.images || []);
	const totalImages = images.length;
	let loadedImages = 0;
	let imagesSettled = totalImages === 0;

	const onOneImageSettled = () => {
		loadedImages += 1;
		if (loadedImages >= totalImages) imagesSettled = true;
	};

	images.forEach((img) => {
		if (img.complete && img.naturalWidth > 0) {
			onOneImageSettled();
			return;
		}
		const handler = () => {
			img.removeEventListener("load", handler);
			img.removeEventListener("error", handler);
			onOneImageSettled();
		};
		img.addEventListener("load", handler, { passive: true });
		img.addEventListener("error", handler, { passive: true });
	});

	let fontsReady = false;
	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(
			() => {
				fontsReady = true;
			},
			() => {
				fontsReady = true;
			},
		);
	} else {
		fontsReady = true;
	}

	let windowLoaded = false;
	window.addEventListener(
		"load",
		() => {
			windowLoaded = true;
		},
		{ once: true },
	);

	let rafId = 0;
	const tick = () => {
		const imgProgress = totalImages ? loadedImages / totalImages : 1;
		const fontProgress = fontsReady ? 1 : 0;
		const loadProgress = windowLoaded ? 1 : 0;

		// 0..1 avec une fin réservée à l'événement "load" (ressenti plus naturel)
		const composite = clamp01(imgProgress * 0.72 + fontProgress * 0.18 + loadProgress * 0.1);

		// Tant que la page n'est pas totalement prête, on évite d'afficher 100%
		const display = windowLoaded ? 1 : Math.min(0.98, composite);
		setProgress(display);

		if (!windowLoaded) {
			rafId = requestAnimationFrame(tick);
			return;
		}

		const elapsed = performance.now() - startTime;
		const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
		window.setTimeout(() => {
			root.classList.remove("is-loading");
			const cleanup = () => {
				loaderEl.removeEventListener("transitionend", cleanup);
				loaderEl.remove();
			};
			loaderEl.addEventListener("transitionend", cleanup);
			// fallback au cas où le transitionend ne part pas
			window.setTimeout(cleanup, 1200);
		}, remaining);
	};

	setProgress(0);
	rafId = requestAnimationFrame(tick);

	return () => {
		if (rafId) cancelAnimationFrame(rafId);
	};
};

setupPageLoader();

window.addEventListener("load", () => {
	const ensureSectionLine = (sectionEl) => {
		if (!sectionEl) return null;
		let lineEl = sectionEl.querySelector(".section-line");
		if (!lineEl) {
			lineEl = document.createElement("div");
			lineEl.className = "section-line";
			sectionEl.appendChild(lineEl);
		}
		return lineEl;
	};

	const sectionHero = document.querySelector("#hero");
	const sectionBigTitle = document.querySelector("#section-big-title");
	const sectionTimeline = document.querySelector("#section-timeline");
	const sectionThanks = document.querySelector("#section-thanks");
	const sectionMarquee = document.querySelector("#section-marquee");

	const heroVideoEl = sectionHero ? sectionHero.querySelector(".hero-banner__pic video") : null;
	const heroFallbackImgEl = sectionHero ? sectionHero.querySelector(".hero-banner__pic img") : null;
	const heroMediaEl = heroVideoEl || heroFallbackImgEl;
	const bigTitleEl = sectionBigTitle ? sectionBigTitle.querySelector(".big-title") : null;
	const bigTitleContainerEl = sectionBigTitle ? sectionBigTitle.querySelector(".big-title-ctn") : null;
	const bigTitlePicsEl = sectionBigTitle ? sectionBigTitle.querySelector(".big-title__pics") : null;

	const footerEl = document.querySelector(".footer");
	let refreshRafId = 0;

	const scheduleScrollRefresh = () => {
		if (refreshRafId) cancelAnimationFrame(refreshRafId);
		refreshRafId = requestAnimationFrame(() => {
			refreshRafId = 0;
			ScrollTrigger.refresh();
		});
	};

	const initScrollSmoother = () => {
		const prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const lowMemory = typeof navigator !== "undefined" && navigator.deviceMemory ? navigator.deviceMemory <= 4 : false;
		const lowCores = typeof navigator !== "undefined" && navigator.hardwareConcurrency ? navigator.hardwareConcurrency <= 4 : false;

		if (prefersReducedMotion || lowMemory || lowCores) return;

		ScrollSmoother.create({
			wrapper: "#smooth-wrapper",
			content: "#smooth-content",
			smooth: 2,
			effects: true,
			smoothTouch: 0.1,
			normalizeScroll: true,
			fixedMarkers: true,
		});
	};

	const initHeroTitle = () => {
		const heroTitleSans = document.querySelector(".hero-heading__sans");
		const heroTitleSerif = document.querySelector(".hero-heading__serif");
		const heroCaptionEls = Array.from(document.querySelectorAll(".hero-caption p"));
		const heroScrollEls = Array.from(document.querySelectorAll(".hero-scroll > *"));
		if (!heroTitleSans) return;
		if (document.fonts && document.fonts.status !== "loaded") {
			document.fonts.ready.then(() => initHeroTitle());
			return;
		}

		const split = new SplitText(heroTitleSans, {
			type: "lines, chars",
			linesClass: "line",
			charsClass: "char",
		});

		const lines = split.lines;
		const chars = split.chars;

		// créer les masks et définir leur origine selon ligne
		lines.forEach((line, i) => {
			const mask = document.createElement("div");
			mask.classList.add("line-mask");
			mask.style.transformOrigin = i % 2 === 0 ? "left" : "right"; // alternance
			line.appendChild(mask);
		});

		const masks = heroTitleSans.querySelectorAll(".line-mask");

		// état initial : toutes les lettres invisibles
		gsap.set(lines, { opacity: 0, x: 50 });
		gsap.set(chars, { x: 80, opacity: 0, filter: "blur(26px)" });
		gsap.set(masks, { scaleX: 1 });

		// timeline principale
		const tl = gsap.timeline({ delay: 0 });
		const lineOverlap = 0.8; // chevauchement entre lignes

		const captionLines = [];
		heroCaptionEls.forEach((captionEl) => {
			const captionSplit = new SplitText(captionEl, {
				type: "lines",
				linesClass: "line",
			});
			captionLines.push(...captionSplit.lines);
		});

		if (captionLines.length) {
			gsap.set(captionLines, { opacity: 0, y: 16 });
		}

		if (heroScrollEls.length) {
			gsap.set(heroScrollEls, { opacity: 0, y: 20 });
		}

		if (captionLines.length) {
			tl.to(
				captionLines,
				{
					opacity: 1,
					y: 0,
					duration: 0.7,
					ease: "power3.out",
					stagger: 0.08,
				},
				">",
			);
		}

		// animer chaque ligne séquentiellement
		lines.forEach((line, i) => {
			const lineChars = line.querySelectorAll(".char");
			const mask = line.querySelector(".line-mask");

			tl.to(
				line,
				{
					opacity: 1,
					x: 0,
					duration: 1,
					ease: "power3.inOut",
				},
				i === 0 ? "-=0.5" : `-=${lineOverlap}`,
			);

			tl.to(
				mask,
				{
					scaleX: 0,
					duration: 1,
					ease: "power3.inOut",
				},
				"<",
			);

			tl.to(
				lineChars,
				{
					x: 0,
					opacity: 1,
					filter: "blur(0px)",
					duration: 1,
					ease: "power4.out",
					stagger: 0.02,
				},
				"-=0.8", // chevauchement partiel avec le mask
			);
		});

		if (heroTitleSerif) {
			const serifSplit = new SplitText(heroTitleSerif, {
				type: "chars",
				charsClass: "char",
			});

			const serifChars = serifSplit.chars;
			gsap.set(serifChars, { opacity: 0, y: 10 });

			tl.to(
				serifChars,
				{
					y: 0,
					opacity: 1,
					duration: 0.8,
					ease: "power4.out",
					stagger: 0.08,
				},
				"-=0.5",
			);
		}

		if (heroScrollEls.length) {
			tl.to(
				heroScrollEls,
				{
					opacity: 1,
					y: 0,
					duration: 0.7,
					ease: "power3.out",
					stagger: 0.1,
				},
				"-=0.15",
			);
		}
	};

	const initHero = () => {
		if (!sectionHero || !heroMediaEl) return;
		const headingSpans = document.querySelectorAll(".reveal-mask span");
		const heroDissolvePreset = "medium"; // "soft" | "medium"
		const heroDissolve = {
			soft: {
				filter: "blur(14px) brightness(1.2) saturate(0.55) contrast(0.9)",
				scale: 1.08,
			},
			medium: {
				filter: "blur(22px) brightness(1.5) saturate(0.35) contrast(0.82)",
				scale: 1.2,
			},
		}[heroDissolvePreset];

		if (heroVideoEl) {
			const markPlaying = () => sectionHero.classList.add("is-video-playing");
			heroVideoEl.addEventListener("playing", markPlaying, { passive: true, once: true });
			const playPromise = heroVideoEl.play();
			if (playPromise && typeof playPromise.catch === "function") {
				playPromise.catch(() => {
					// Autoplay peut être bloqué: on laisse l'image fallback visible.
				});
			}
		}

		gsap.set(heroMediaEl, {
			opacity: 1,
			filter: "blur(0px) brightness(1) saturate(1) contrast(1)",
			scale: 1,
		});
		gsap.set(sectionHero, {
			"--_c": "#ffffff",
			"--_title-serif-c": "var(--color-primary)",
			"--hero-overlay-alpha": 0.35,
			backgroundColor: "transparent",
		});

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: sectionHero,
				start: "top top",
				end: "+=150%",
				pin: true,
				pinSpacing: true,
				scrub: true,
				anticipatePin: 1,
				invalidateOnRefresh: true,
			},
		});

		const heroLineEl = ensureSectionLine(sectionHero);
		if (heroLineEl) {
			gsap.set(heroLineEl, { scaleY: 0, transformOrigin: "top center" });
		}

		tl.to(headingSpans, {
			y: 0, // Texte remonte
			duration: 1,
			ease: "power3.out",
			stagger: 0.2, // Déphasage entre les mots/éléments
		});

		tl.fromTo(
			heroMediaEl,
			{
				opacity: 1,
				filter: "blur(0px) brightness(1) saturate(1) contrast(1)",
				scale: 1,
			},
			{
				opacity: 0,
				filter: heroDissolve.filter,
				scaleX: heroDissolve.scale,
				immediateRender: false,
				ease: "none",
			},
			0,
		);

		tl.fromTo(
			sectionHero,
			{
				"--hero-overlay-alpha": 0.35,
			},
			{
				"--hero-overlay-alpha": 0,
				immediateRender: false,
				ease: "none",
			},
			0,
		);

		if (heroLineEl) {
			tl.to(
				heroLineEl,
				{
					scaleY: 1,
					ease: "none",
				},
				0.15,
			);
		}

		tl.fromTo(
			sectionHero,
			{
				"--_c": "#ffffff",
				backgroundColor: "transparent",
			},
			{
				"--_c": "#111111",
				backgroundColor: "transparent",
				immediateRender: false,
				ease: "none",
			},
			0,
		);
	};

	const initBigTitle = () => {
		if (!sectionBigTitle || !bigTitleEl || !bigTitleContainerEl || !bigTitlePicsEl) return;

		if (bigTitleEl._splitText) {
			bigTitleEl._splitText.revert();
		}
		const splitBigTitle = new SplitText(bigTitleEl, { type: "words, chars" });
		bigTitleEl._splitText = splitBigTitle;
		const titleCharEls = splitBigTitle.chars;

		const setBigTitleBackgroundPosition = gsap.quickSetter(sectionBigTitle, "backgroundPosition");

		const bigTitleScrollMultiplier = 1.2;
		let bigTitleScrollDistance = 0;

		const measureBigTitleDistances = () => {
			const picsWidth = bigTitlePicsEl.scrollWidth;
			const titleWidth = bigTitleEl.scrollWidth;
			const vw = window.innerWidth;
			bigTitleScrollDistance = Math.max(picsWidth, titleWidth, vw);
		};

		const updateBigTitleBackground = (overallProgress) => {
			const posX = 50 + overallProgress * 50;
			setBigTitleBackgroundPosition(`${posX}% 50%`);
		};

		// --- LOGIQUE COVERFLOW EXTRAITE ---
		const bigTitlePicEls = Array.from(bigTitlePicsEl.querySelectorAll(".project-browser"));

		const updateCoverflow = () => {
			const centerX = window.innerWidth / 2;
			const influenceArea = window.innerWidth * 0.6;

			bigTitlePicEls.forEach((picEl) => {
				const rect = picEl.getBoundingClientRect();
				const picCenter = rect.left + rect.width / 2;
				const distanceFromCenter = picCenter - centerX;

				let rawRatio = distanceFromCenter / influenceArea;
				rawRatio = Math.max(-1, Math.min(1, rawRatio));

				const absRatio = Math.abs(rawRatio);
				const smoothRatio = 1 - Math.pow(absRatio, 2);

				const finalScale = 1 + smoothRatio * 0.08;
				const rotationY = rawRatio * -25;
				const zIndex = Math.round(smoothRatio * 100);

				const blurAmount = (1 - smoothRatio) * 4;
				const brightness = 0.4 + smoothRatio * 0.6;

				gsap.set(picEl, {
					scale: finalScale,
					rotationY: rotationY,
					z: smoothRatio * 100,
					zIndex: zIndex,
					filter: `blur(${blurAmount}px) brightness(${brightness})`,
					opacity: 0.3 + smoothRatio * 0.7,
					transformPerspective: 1000,
					overwrite: "auto",
				});
			});
		};
		// ----------------------------------

		// Point de départ symétrique
		gsap.set(bigTitleContainerEl, { x: () => window.innerWidth / 2 });
		gsap.set(bigTitlePicsEl, { x: () => -(bigTitlePicsEl.scrollWidth - window.innerWidth / 2) });
		gsap.set(titleCharEls, { y: 40, opacity: 0 });
		gsap.set(bigTitlePicEls, { scale: 1, transformOrigin: "50% 50%" });

		const revealInitiallyVisibleChars = () => {
			titleCharEls.forEach((charEl) => {
				const rect = charEl.getBoundingClientRect();
				const isVisible = rect.right > 0 && rect.left < window.innerWidth;
				if (isVisible) {
					gsap.set(charEl, { y: 0, opacity: 1 });
				}
			});
		};

		measureBigTitleDistances();

		const horizontalScrollTween = gsap.fromTo(
			bigTitleContainerEl,
			{ x: () => window.innerWidth / 2 },
			{
				x: () => -(bigTitleEl.scrollWidth - window.innerWidth / 2),
				ease: "none",
				duration: 1,
				immediateRender: false,
				onUpdate() {
					updateBigTitleBackground(this.progress());
				},
				scrollTrigger: {
					trigger: sectionBigTitle,
					start: "top top",
					end: () => `+=${bigTitleScrollDistance * bigTitleScrollMultiplier}`,
					pin: true,
					scrub: true,
					invalidateOnRefresh: true,
				},
			},
		);

		const horizontalPicsTween = gsap.fromTo(
			bigTitlePicsEl,
			{ x: () => -(bigTitlePicsEl.scrollWidth - window.innerWidth / 2) },
			{
				x: () => window.innerWidth / 2,
				ease: "none",
				duration: 1,
				scrollTrigger: {
					trigger: sectionBigTitle,
					start: "top top",
					end: () => `+=${bigTitleScrollDistance * bigTitleScrollMultiplier}`,
					scrub: true,
					invalidateOnRefresh: true,
					onUpdate: updateCoverflow, // Appelée à chaque frame de scroll
				},
			},
		);

		// Initialisation
		revealInitiallyVisibleChars();

		// On force le premier calcul immédiatement pour l'état initial
		updateCoverflow();

		const refreshHandler = () => {
			measureBigTitleDistances();
			revealInitiallyVisibleChars();
			updateCoverflow(); // Recalcule les positions après un resize/refresh
		};

		if (sectionBigTitle._bigTitleRefreshHandler) {
			ScrollTrigger.removeEventListener("refresh", sectionBigTitle._bigTitleRefreshHandler);
		}
		sectionBigTitle._bigTitleRefreshHandler = refreshHandler;
		ScrollTrigger.addEventListener("refresh", refreshHandler);

		// Triggers pour les lettres
		const buildHorizontalTrigger = (triggerEl, start, extraOptions = {}) => ({
			trigger: triggerEl,
			containerAnimation: horizontalScrollTween,
			start,
			...extraOptions,
		});

		titleCharEls.forEach((charEl) => {
			gsap.to(charEl, {
				y: 0,
				opacity: 1,
				duration: 0.8,
				ease: "back.out(2.5)",
				overwrite: "auto",
				scrollTrigger: buildHorizontalTrigger(charEl, "left 95%", {
					toggleActions: "play none none reverse",
					invalidateOnRefresh: true,
				}),
			});
		});
	};

	const initBigTitleMobileSlider = () => {
		if (!sectionBigTitle || !bigTitlePicsEl) return () => {};

		const picElsInDomOrder = Array.from(bigTitlePicsEl.querySelectorAll(".project-browser"));
		if (!picElsInDomOrder.length) return () => {};

		const originalParent = bigTitlePicsEl;
		const trackEl = document.createElement("div");
		trackEl.className = "big-title__pics-track swiper-wrapper";

		let paginationEl = sectionBigTitle.querySelector(".big-title__pagination");
		const hasExistingPagination = Boolean(paginationEl);
		if (!paginationEl) {
			paginationEl = document.createElement("div");
			paginationEl.className = "big-title__pagination swiper-pagination";
		}

		bigTitlePicsEl.classList.add("swiper");

		const picElsForMobile = [...picElsInDomOrder].reverse();
		picElsForMobile.forEach((picEl) => {
			picEl.classList.add("swiper-slide");
			trackEl.appendChild(picEl);
		});

		bigTitlePicsEl.appendChild(trackEl);
		if (!hasExistingPagination) {
			bigTitlePicsEl.appendChild(paginationEl);
		}

		const slider = new Swiper(bigTitlePicsEl, {
			modules: [Pagination],
			slidesPerView: 1.15,
			spaceBetween: 16,
			centeredSlides: true,
			loop: picElsInDomOrder.length > 2,
			grabCursor: true,
			pagination: {
				el: paginationEl,
				clickable: true,
				dynamicBullets: true,
			},
			breakpoints: {
				540: {
					slidesPerView: 1.35,
					spaceBetween: 16,
				},
			},
		});

		return () => {
			slider.destroy(true, true);
			picElsInDomOrder.forEach((picEl) => {
				picEl.classList.remove("swiper-slide");
				originalParent.appendChild(picEl);
			});
			trackEl.remove();
			bigTitlePicsEl.classList.remove("swiper");
			if (!hasExistingPagination && paginationEl) {
				paginationEl.remove();
			}
		};
	};
	const initTimelinePolaroid = () => {
		const wall = document.getElementById("timeline-polaroid");
		if (!wall) return;

		const cards = wall.querySelectorAll(".timeline-polaroid__card");
		const lightbox = document.getElementById("timeline-lightbox");
		if (!lightbox || !cards.length) return;

		const lightboxContent = lightbox.querySelector(".timeline-lightbox__content");
		const lightboxImage = document.getElementById("timeline-lightbox-image");
		const lightboxCaption = document.getElementById("timeline-lightbox-caption");
		const lightboxYear = document.getElementById("timeline-lightbox-year");
		const lightboxPrevButton = document.getElementById("timeline-lightbox-prev");
		const lightboxNextButton = document.getElementById("timeline-lightbox-next");
		const prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const lightboxItems = [];
		let currentIndex = -1;
		let touchStartX = 0;
		let touchStartY = 0;
		let isTransitioning = false;
		let hoverTopZ = cards.length + 300;
		let isDraggingCard = false;
		let lastLayoutWidth = 0;
		let lastLayoutIsMobile = null;
		const setDragScrollLock = (locked) => {
			document.documentElement.classList.toggle("is-polaroid-dragging", locked);
		};
		const hoverZResetTimers = new WeakMap();
		const hoverZRiseTimers = new WeakMap();
		const getTopZIndex = () =>
			Array.from(cards).reduce((top, item) => {
				const z = parseInt(item.style.zIndex || item.dataset.baseZ || "0", 10);
				return Number.isFinite(z) ? Math.max(top, z) : top;
			}, hoverTopZ);
		const bringCardToFront = (card) => {
			hoverTopZ = Math.max(hoverTopZ + 1, getTopZIndex() + 1);
			const z = String(hoverTopZ);
			card.style.zIndex = z;
			return z;
		};

		const clearHoverZRise = (card) => {
			const timer = hoverZRiseTimers.get(card);
			if (timer) {
				window.clearTimeout(timer);
				hoverZRiseTimers.delete(card);
			}
		};

		const clearHoverZReset = (card) => {
			const timer = hoverZResetTimers.get(card);
			if (timer) {
				window.clearTimeout(timer);
				hoverZResetTimers.delete(card);
			}
		};

		const scheduleHoverZReset = (card, delay = 170) => {
			clearHoverZReset(card);
			const timer = window.setTimeout(() => {
				hoverZResetTimers.delete(card);
				if (card.matches(":hover") || card.classList.contains("is-dragging")) return;
				card.style.zIndex = card.dataset.baseZ || "10";
			}, delay);
			hoverZResetTimers.set(card, timer);
		};

		const scheduleHoverZRise = (card, delay = 70) => {
			clearHoverZRise(card);
			const timer = window.setTimeout(() => {
				hoverZRiseTimers.delete(card);
				if (!card.matches(":hover") || card.classList.contains("is-dragging")) return;
				bringCardToFront(card);
			}, delay);
			hoverZRiseTimers.set(card, timer);
		};

		const intersects = (a, b) => !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);

		const showImageAt = (index) => {
			if (!lightboxItems.length) return;
			const safeIndex = (index + lightboxItems.length) % lightboxItems.length;
			const item = lightboxItems[safeIndex];
			currentIndex = safeIndex;
			lightboxImage.src = item.src;
			lightboxCaption.textContent = item.caption || "";
			lightboxYear.textContent = item.year || "";
			lightboxYear.style.display = item.year ? "inline-block" : "none";
		};

		const switchImage = (direction) => {
			if (isTransitioning || currentIndex === -1) return;
			isTransitioning = true;

			const exitClass = direction === "left" ? "is-exiting-left" : "is-exiting-right";
			const enterClass = direction === "left" ? "is-entering-right" : "is-entering-left";
			const targetIndex = direction === "left" ? currentIndex + 1 : currentIndex - 1;
			lightboxContent.classList.add(exitClass);

			window.setTimeout(() => {
				showImageAt(targetIndex);
				lightboxContent.classList.remove(exitClass);
				lightboxContent.classList.add(enterClass);
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						lightboxContent.classList.remove(enterClass);
						window.setTimeout(() => {
							isTransitioning = false;
						}, 220);
					});
				});
			}, 160);
		};

		const showPreviousImage = () => {
			if (currentIndex === -1) return;
			switchImage("right");
		};

		const showNextImage = () => {
			if (currentIndex === -1) return;
			switchImage("left");
		};

		const openLightboxAt = (index) => {
			showImageAt(index);
			lightbox.classList.add("is-open");
			lightbox.setAttribute("aria-hidden", "false");
		};

		const closeLightbox = () => {
			lightbox.classList.remove("is-open");
			lightbox.setAttribute("aria-hidden", "true");
			window.setTimeout(() => {
				if (!lightbox.classList.contains("is-open")) {
					lightboxImage.src = "";
					lightboxCaption.textContent = "";
					lightboxYear.textContent = "";
					lightboxYear.style.display = "none";
					currentIndex = -1;
				}
			}, 300);
		};

		const layoutCards = () => {
			const wallWidth = wall.clientWidth;
			const isMobile = window.innerWidth <= 768;
			const cardWidth = isMobile ? 154 : 220;
			const cardHeight = isMobile ? 184 : 260;
			const horizontalInset = isMobile ? 8 : 20;
			const topInset = 0;
			const centerYOffset = 0;
			const wallHeight = window.innerHeight;

			wall.style.minHeight = "0";
			const centerX = wallWidth / 2;
			const centerY = wallHeight / 2 + centerYOffset;
			const spreadX = isMobile ? Math.min(wallWidth - cardWidth - horizontalInset * 2, wallWidth * 0.92) : Math.min(wallWidth - cardWidth - horizontalInset * 2, wallWidth * 0.95);
			const spreadY = isMobile ? Math.min(280 + cards.length * 14, wallHeight * 0.5) : Math.min(260 + cards.length * 12, wallHeight * 0.44);
			const maxAngle = isMobile ? 22 : 34;
			const placedRects = [];
			const placements = [];
			const zIndexes = Array.from({ length: cards.length }, (_, i) => 10 + i);
			const minGap = isMobile ? 4 : 8;

			for (let i = zIndexes.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[zIndexes[i], zIndexes[j]] = [zIndexes[j], zIndexes[i]];
			}

			cards.forEach((card, index) => {
				const maxX = Math.max(horizontalInset, wallWidth - cardWidth - horizontalInset);
				const maxY = Math.max(topInset, wallHeight - cardHeight - 20);
				let x = horizontalInset;
				let y = topInset;
				let bestCandidate = null;
				let bestOverlap = Number.POSITIVE_INFINITY;

				for (let attempt = 0; attempt < 260; attempt++) {
					const candidateX = Math.max(horizontalInset, Math.min(maxX, centerX - cardWidth / 2 + (Math.random() - 0.5) * spreadX));
					const candidateY = Math.max(topInset, Math.min(maxY, centerY - cardHeight / 2 + (Math.random() - 0.5) * spreadY));
					const candidateRect = { left: candidateX, top: candidateY, right: candidateX + cardWidth, bottom: candidateY + cardHeight };
					const candidateRectWithGap = {
						left: candidateRect.left - minGap,
						top: candidateRect.top - minGap,
						right: candidateRect.right + minGap,
						bottom: candidateRect.bottom + minGap,
					};
					let overlapCount = 0;
					let valid = true;
					for (let i = 0; i < placedRects.length; i++) {
						if (!intersects(candidateRectWithGap, placedRects[i])) continue;
						overlapCount++;
						valid = false;
						break;
					}
					if (valid) {
						x = candidateX;
						y = candidateY;
						bestCandidate = candidateRect;
						break;
					}
					if (overlapCount < bestOverlap) {
						bestOverlap = overlapCount;
						x = candidateX;
						y = candidateY;
						bestCandidate = candidateRect;
					}
				}

				const angle = 5 + Math.random() * (maxAngle - 5);
				const sign = Math.random() < 0.5 ? -1 : 1;
				const z = zIndexes[index];
				const rect = bestCandidate || { left: x, top: y, right: x + cardWidth, bottom: y + cardHeight };
				placedRects.push(rect);
				card.style.left = x + "px";
				card.style.top = y + "px";
				card.style.zIndex = String(z);
				card.dataset.baseZ = String(z);
				card.style.setProperty("--rotate", (angle * sign).toFixed(2) + "deg");
				placements.push({ card, x, y, z });
			});

			let minTop = Number.POSITIVE_INFINITY;
			let maxBottom = 0;
			let minLeft = Number.POSITIVE_INFINITY;
			let maxRight = 0;
			placements.forEach(({ y }) => {
				if (y < minTop) minTop = y;
				if (y + cardHeight > maxBottom) maxBottom = y + cardHeight;
			});
			placements.forEach(({ x }) => {
				if (x < minLeft) minLeft = x;
				if (x + cardWidth > maxRight) maxRight = x + cardWidth;
			});

			const clusterCenterY = Number.isFinite(minTop) ? (minTop + maxBottom) / 2 : wallHeight / 2;
			const targetOffsetY = wallHeight / 2 - clusterCenterY;
			const minOffsetY = Number.isFinite(minTop) ? topInset - minTop : targetOffsetY;
			const maxOffsetY = Number.isFinite(maxBottom) ? wallHeight - maxBottom : targetOffsetY;
			const offsetY = Math.max(minOffsetY, Math.min(maxOffsetY, targetOffsetY));
			const clusterCenterX = Number.isFinite(minLeft) && Number.isFinite(maxRight) ? (minLeft + maxRight) / 2 : wallWidth / 2;
			let offsetX = wallWidth / 2 - clusterCenterX;

			if (Number.isFinite(minLeft) && minLeft + offsetX < horizontalInset) {
				offsetX = horizontalInset - minLeft;
			}
			if (Number.isFinite(maxRight) && maxRight + offsetX > wallWidth - horizontalInset) {
				offsetX = wallWidth - horizontalInset - maxRight;
			}

			placements.forEach(({ card, x, y, z }) => {
				card.style.left = x + offsetX + "px";
				card.style.top = y + offsetY + "px";
				card.style.zIndex = String(z);
				card.dataset.baseZ = String(z);
			});

			const compactHeight = Math.max(0, maxBottom - (Number.isFinite(minTop) ? minTop : 0));
			wall.style.minHeight = Math.max(wallHeight, compactHeight) + "px";

			lastLayoutWidth = window.innerWidth;
			lastLayoutIsMobile = isMobile;
		};

		const initPolaroidParallax = () => {
			if (prefersReducedMotion) return;

			cards.forEach((card) => {
				const isMobile = window.innerWidth <= 768;
				const range = isMobile ? 8 + Math.random() * 6 : 10 + Math.random() * 10;
				const shift = (Math.random() < 0.5 ? -1 : 1) * range;

				gsap.fromTo(
					card,
					{ "--parallax-y": `${-shift}px` },
					{
						"--parallax-y": `${shift}px`,
						ease: "none",
						scrollTrigger: {
							trigger: sectionTimeline || wall,
							start: "top bottom",
							end: "bottom top",
							scrub: true,
							invalidateOnRefresh: true,
						},
					},
				);
			});
		};

		cards.forEach((card, index) => {
			const image = card.querySelector("img");
			if (image) {
				image.setAttribute("draggable", "false");
				image.addEventListener("dragstart", (dragEvent) => {
					dragEvent.preventDefault();
				});
				lightboxItems.push({
					src: image.src,
					caption: image.dataset.caption || "",
					year: image.dataset.year || "",
				});
			}

			card.addEventListener("mouseenter", () => {
				clearHoverZReset(card);
				scheduleHoverZRise(card);
			});

			card.addEventListener("mouseleave", () => {
				clearHoverZRise(card);
				scheduleHoverZReset(card);
			});

			card.addEventListener("click", () => {
				if (isDraggingCard || !lightboxItems[index]) return;
				openLightboxAt(index);
			});

			card.addEventListener("pointerdown", (event) => {
				if (event.button !== 0 && event.pointerType !== "touch" && event.pointerType !== "pen") return;
				event.preventDefault();
				clearHoverZReset(card);
				clearHoverZRise(card);

				const cardRect = card.getBoundingClientRect();
				const wallRect = wall.getBoundingClientRect();
				const shiftX = event.clientX - cardRect.left;
				const shiftY = event.clientY - cardRect.top;
				let moved = false;
				let velocityX = 0;
				let velocityY = 0;
				let lastClientX = event.clientX;
				let lastClientY = event.clientY;
				let lastMoveTime = performance.now();

				bringCardToFront(card);
				card.style.cursor = "grabbing";
				card.classList.add("is-dragging");
				if (event.pointerType === "touch" || event.pointerType === "pen") {
					setDragScrollLock(true);
				}

				const maxLeft = () => Math.max(0, wall.clientWidth - card.offsetWidth);
				const maxTop = () => Math.max(0, wall.clientHeight - card.offsetHeight);

				const onPointerMove = (moveEvent) => {
					if (moveEvent.cancelable) moveEvent.preventDefault();
					const now = performance.now();
					const deltaTime = Math.max(1, now - lastMoveTime);
					const nextLeft = Math.min(Math.max(0, moveEvent.clientX - wallRect.left - shiftX), maxLeft());
					const nextTop = Math.min(Math.max(0, moveEvent.clientY - wallRect.top - shiftY), maxTop());
					card.style.left = nextLeft + "px";
					card.style.top = nextTop + "px";
					velocityX = (moveEvent.clientX - lastClientX) / deltaTime;
					velocityY = (moveEvent.clientY - lastClientY) / deltaTime;
					lastClientX = moveEvent.clientX;
					lastClientY = moveEvent.clientY;
					lastMoveTime = now;

					if (!moved) {
						const distance = Math.hypot(moveEvent.clientX - event.clientX, moveEvent.clientY - event.clientY);
						if (distance > 4) {
							moved = true;
							isDraggingCard = true;
						}
					}
				};

				const onPointerUp = () => {
					if (card.releasePointerCapture) {
						card.releasePointerCapture(event.pointerId);
					}
					card.classList.remove("is-dragging");
					card.style.cursor = "";
					setDragScrollLock(false);

					if (moved) {
						const pinnedZ = bringCardToFront(card);
						card.style.zIndex = pinnedZ;
						card.dataset.baseZ = pinnedZ;
						const currentLeft = parseFloat(card.style.left) || 0;
						const currentTop = parseFloat(card.style.top) || 0;
						const throwX = Math.max(-120, Math.min(120, velocityX * 140));
						const throwY = Math.max(-120, Math.min(120, velocityY * 140));
						const finalLeft = Math.min(Math.max(0, currentLeft + throwX), maxLeft());
						const finalTop = Math.min(Math.max(0, currentTop + throwY), maxTop());
						const currentAngle = parseFloat(card.dataset.rotate || "0");
						const angleDelta = Math.max(-8, Math.min(8, velocityX * 22));
						const finalAngle = Math.max(-45, Math.min(45, currentAngle + angleDelta));

						card.dataset.rotate = finalAngle.toFixed(2);
						card.style.setProperty("--rotate", card.dataset.rotate + "deg");

						gsap.to(card, {
							left: finalLeft,
							top: finalTop,
							duration: 0.38,
							ease: "power2.out",
							overwrite: "auto",
						});
						gsap.to(card, {
							rotation: finalAngle,
							duration: 0.32,
							ease: "power2.out",
							overwrite: "auto",
						});
					}

					card.removeEventListener("pointermove", onPointerMove);
					card.removeEventListener("pointerup", onPointerUp);
					card.removeEventListener("pointercancel", onPointerUp);
					window.setTimeout(() => {
						isDraggingCard = false;
					}, 0);

					scheduleHoverZReset(card, 120);
				};

				if (card.setPointerCapture) {
					card.setPointerCapture(event.pointerId);
				}
				card.addEventListener("pointermove", onPointerMove);
				card.addEventListener("pointerup", onPointerUp);
				card.addEventListener("pointercancel", onPointerUp);
			});
		});

		let resizeTimer;
		window.addEventListener("resize", () => {
			if (isDraggingCard) return;
			window.clearTimeout(resizeTimer);
			resizeTimer = window.setTimeout(() => {
				const isMobile = window.innerWidth <= 768;
				// On mobile, ignore pure height-only resizes (address bar show/hide) to avoid reshuffling.
				if (isMobile && lastLayoutIsMobile === true && Math.abs(window.innerWidth - lastLayoutWidth) < 2) return;
				layoutCards();
			}, 150);
		});

		lightbox.addEventListener("click", (event) => {
			if (event.target !== lightbox) return;
			closeLightbox();
		});

		if (lightboxPrevButton) {
			lightboxPrevButton.addEventListener("click", (event) => {
				event.stopPropagation();
				showPreviousImage();
			});
		}

		if (lightboxNextButton) {
			lightboxNextButton.addEventListener("click", (event) => {
				event.stopPropagation();
				showNextImage();
			});
		}

		document.addEventListener("keydown", (event) => {
			if (!lightbox.classList.contains("is-open")) return;
			if (event.key === "Escape") {
				closeLightbox();
				return;
			}
			if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
				event.preventDefault();
				showPreviousImage();
				return;
			}
			if (event.key === "ArrowRight" || event.key === "ArrowDown") {
				event.preventDefault();
				showNextImage();
			}
		});

		lightboxContent.addEventListener(
			"touchstart",
			(event) => {
				if (!lightbox.classList.contains("is-open") || !event.changedTouches.length) return;
				touchStartX = event.changedTouches[0].clientX;
				touchStartY = event.changedTouches[0].clientY;
			},
			{ passive: true },
		);

		lightboxContent.addEventListener(
			"touchend",
			(event) => {
				if (!lightbox.classList.contains("is-open") || !event.changedTouches.length) return;
				const touchEndX = event.changedTouches[0].clientX;
				const touchEndY = event.changedTouches[0].clientY;
				const deltaX = touchEndX - touchStartX;
				const deltaY = touchEndY - touchStartY;
				if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
				if (deltaX > 0) showPreviousImage();
				else showNextImage();
			},
			{ passive: true },
		);

		layoutCards();
		initPolaroidParallax();
	};

	const initTimelineHeading = () => {
		if (!sectionTimeline) return;
		const timelineHeadingParts = Array.from(sectionTimeline.querySelectorAll(".timeline__heading > *"));
		if (!timelineHeadingParts.length) return;

		gsap.set(timelineHeadingParts, { y: 40, opacity: 0 });

		const timelineHeadingTimeline = gsap.timeline().to(timelineHeadingParts, {
			y: 0,
			opacity: 1,
			duration: 0.8,
			ease: "power3.out",
			stagger: 0.12,
		});

		ScrollTrigger.create({
			trigger: sectionTimeline,
			animation: timelineHeadingTimeline,
			start: "top 80%",
			end: "bottom 50%",
			scrub: true,
			invalidateOnRefresh: true,
		});
	};

	const initThanks = () => {
		if (!sectionThanks) return;
		const thanksHeadingParts = Array.from(sectionThanks.querySelectorAll(".thanks__heading > *"));
		const thanksImageWrap = sectionThanks.querySelector(".tanks__img");
		const thanksCircle = sectionThanks.querySelector(".thanks__circle");

		gsap.set(thanksHeadingParts, { y: 40, opacity: 0 });
		gsap.set(thanksImageWrap, {
			y: 36,
			opacity: 0,
			scale: 0.94,
			clipPath: "inset(0% 10% 0% 10%)",
		});
		gsap.set(thanksCircle, { y: 24, opacity: 0, scale: 0.7, rotate: -18 });

		const thanksTimeline = gsap
			.timeline()
			.to(thanksHeadingParts, {
				y: 0,
				opacity: 1,
				duration: 0.8,
				ease: "power3.out",
				stagger: 0.12,
			})
			.to(
				thanksImageWrap,
				{
					y: 0,
					opacity: 1,
					scale: 1,
					clipPath: "inset(0% 0% 0% 0%)",
					duration: 1,
					ease: "power3.out",
				},
				"-=0.35",
			)
			.to(
				thanksCircle,
				{
					y: 0,
					opacity: 1,
					scale: 1,
					rotate: 0,
					duration: 0.7,
					ease: "back.out(1.9)",
				},
				"-=0.55",
			);

		ScrollTrigger.create({
			trigger: sectionThanks,
			animation: thanksTimeline,
			start: "top 80%",
			end: "bottom 50%",
			scrub: true,
			invalidateOnRefresh: true,
		});
	};

	const initMarquee = () => {
		if (!sectionMarquee) return;
		const marqueeTrack = sectionMarquee.querySelector(".marquee__track");
		if (!marqueeTrack) return;
		const marqueeTitles = Array.from(marqueeTrack.querySelectorAll(".marquee__title"));
		if (marqueeTitles.length === 1) {
			const clone = marqueeTitles[0].cloneNode(true);
			clone.setAttribute("aria-hidden", "true");
			marqueeTrack.appendChild(clone);
		}

		const getMarqueeShift = () => {
			const firstTitle = marqueeTrack.querySelector(".marquee__title");
			if (!firstTitle) return 0;
			const { gap } = getComputedStyle(marqueeTrack);
			return firstTitle.offsetWidth + parseFloat(gap || "0");
		};

		gsap.fromTo(
			marqueeTrack,
			{ x: 0 },
			{
				x: () => -getMarqueeShift(),
				ease: "none",
				scrollTrigger: {
					trigger: sectionMarquee,
					start: "-50% 100%",
					end: "150% 0",
					scrub: true,
					invalidateOnRefresh: true,
				},
			},
		);
	};

	const initFooter = () => {
		if (!footerEl) return;
		const footerContentItems = Array.from(footerEl.querySelectorAll(".footer__content > *"));
		const footerButton = footerEl.querySelector(".btn");

		gsap.set(footerEl, { clipPath: "inset(100% 0% 0% 0%)" });
		gsap.set([footerContentItems, footerButton], {
			y: 48,
			opacity: 0,
		});

		gsap.to(footerEl, {
			clipPath: "inset(0% 0% 0% 0%)",
			ease: "none",
			scrollTrigger: {
				trigger: footerEl,
				start: "top 90%",
				end: "bottom bottom",
				scrub: true,
				invalidateOnRefresh: true,
			},
		});

		const footerTimeline = gsap
			.timeline()
			.to(footerContentItems, {
				y: 0,
				opacity: 1,
				duration: 0.9,
				ease: "power3.out",
				stagger: 0.14,
			})
			.to(
				footerButton,
				{
					y: 0,
					opacity: 1,
					duration: 0.7,
					ease: "back.out(1.7)",
				},
				"-=0.35",
			);

		ScrollTrigger.create({
			trigger: footerEl,
			animation: footerTimeline,
			start: "-30% 50%",
			end: "10% 50%",
			scrub: 1,
			invalidateOnRefresh: true,
		});
	};

	const baseContext = gsap.context(() => {
		initMarquee();
		initTimelinePolaroid();
		initFooter();

		scheduleScrollRefresh();
	});

	const mm = gsap.matchMedia();
	mm.add("(min-width: 769px)", () => {
		const gsapContext = gsap.context(() => {
			initScrollSmoother();
			initHeroTitle();
			initHero();
			initBigTitle();
			initTimelineHeading();
			initThanks();

			scheduleScrollRefresh();
		});

		return () => gsapContext.revert();
	});

	mm.add("(max-width: 768px)", () => {
		initHeroTitle();
		const cleanupMobileSlider = initBigTitleMobileSlider();
		scheduleScrollRefresh();
		return () => cleanupMobileSlider();
	});

	let _lastResizeWidth = window.innerWidth;
	window.addEventListener("resize", () => {
		const w = window.innerWidth;
		if (w !== _lastResizeWidth) {
			_lastResizeWidth = w;
			initHeroTitle();
		}
	});

	window.addEventListener(
		"pagehide",
		() => {
			mm.revert();
			baseContext.revert();
		},
		{ once: true },
	);
});
